import {
  computeAvailableOutput,
  EdgeLike,
} from "@/flow/application/computeAvailableOutput";
import { checkIsLoop } from "@/core/util";
import { getRouterLoopContext } from "@/flow/utils/routerUtils";
import {
  getLoopStackForStep,
  getParentLoopOrigId,
} from "@/flow/utils/loopUtils";

export function useAddPositionToStep(
  steps: Array<any>,
  edges?: Array<EdgeLike>
): Array<any> {
  const newSteps = JSON.parse(JSON.stringify(steps));
  const stepByUniqueId = new Map<string, any>();
  const loopStackCache = new Map<string, string[]>();
  const hasExplicitEdges = Array.isArray(edges) && edges.length > 0;

  const normalizeKey = (value: any): string =>
    value !== undefined && value !== null ? String(value) : "";

  const getCachedLoopStack = (step: any): string[] => {
    if (!step) return [];
    const key = normalizeKey(step.uniqueId ?? step.id ?? step.origId);
    if (key && loopStackCache.has(key)) {
      return loopStackCache.get(key) as string[];
    }
    const stack = getLoopStackForStep(step, newSteps);
    if (key) {
      loopStackCache.set(key, stack);
    }
    return stack;
  };

  const isLoopOutputAllowed = (currentStep: any, outputStep: any): boolean => {
    if (!currentStep || !outputStep) return true;
    const currentStack = getCachedLoopStack(currentStep);
    let outputStack = getCachedLoopStack(outputStep);
    const outputOrigId = normalizeKey(outputStep?.origId);
    if (
      (checkIsLoop(outputStep) || outputStep?.type === "LOOP") &&
      outputOrigId
    ) {
      outputStack = [...outputStack, outputOrigId];
    }
    if (outputStack.length > currentStack.length) return false;
    for (let i = 0; i < outputStack.length; i += 1) {
      if (outputStack[i] !== currentStack[i]) return false;
    }
    return true;
  };

  function addPositionToStep({
    findingList,
    subPosition = 0,
    totalIndex = 0,
    isStep = true,
    conditionPosition = null,
    subStepUniqueId = null,
    routeIndex = 0,
    previousStep = null,
    routerLoopContext = null,
  }) {
    if (!Array.isArray(findingList) || findingList.length === 0) return;

    // Pre-compute suffix router counts to avoid O(n²) getAllRoutes calls.
    const suffixRouterCount: number[] = new Array(findingList.length).fill(0);
    const countRouters = (item: any): number => {
      if (!item) return 0;
      let c = item.type === "ROUTER" ? 1 : 0;
      if (Array.isArray(item.steps))
        item.steps.forEach((s: any) => {
          c += countRouters(s);
        });
      if (Array.isArray(item.routes))
        item.routes.forEach((r: any) => {
          c += countRouters(r);
        });
      return c;
    };
    let rSum = 0;
    for (let i = findingList.length - 1; i >= 0; i--) {
      suffixRouterCount[i] = rSum;
      rSum += countRouters(findingList[i]);
    }

    // Incremental accumulators for available outputs — avoids O(n²) rescanning.
    const cumulativeOutputs: any[] = [];
    const cumulativeMeta: any[] = [];

    // eslint-disable-next-line consistent-return,array-callback-return
    findingList.map((step, index) => {
      const isRoutes = Array.isArray(step.routes) && step?.routes?.length > 0;
      const isRoute = Array.isArray(step.steps) && step?.steps?.length > 0;
      // Hydrate router loop context early so output/loop filtering uses the latest uiData.
      const currentRouterContext = isRoutes ? getRouterLoopContext(step) : null;
      if (!isStep) {
        // eslint-disable-next-line no-param-reassign
        subPosition += 1;
      }
      if (!isRoutes && isStep) {
        step.stepPosition = {
          x: totalIndex + index,
          y: subPosition,
        };
        step.conditionPosition = conditionPosition;
        step.index = totalIndex + index;
      }
      step.uniqueId = `${subStepUniqueId}-${routeIndex}-${index}`;

      if (!subStepUniqueId) {
        step.uniqueId = index;
      }
      if (subStepUniqueId) {
        step.conditionUniqueId = subStepUniqueId;
      }
      step.ix = totalIndex + index;
      step.iy = subPosition;
      if (isStep && index > 0) {
        step.sourceUniqueId = findingList[index - 1]?.uniqueId;
      }

      if (isRoutes) {
        step.afterRouteCount = suffixRouterCount[index];
      }

      const uniqueKey = normalizeKey(step.uniqueId);
      if (uniqueKey) {
        stepByUniqueId.set(uniqueKey, step);
      }

      if (
        isStep &&
        routerLoopContext?.insideLoop &&
        routerLoopContext?.parentLoopOrigId
      ) {
        if (!step.insideLoop) {
          step.insideLoop = true;
        }
        if (!step.parentLoopOrigId) {
          step.parentLoopOrigId = routerLoopContext.parentLoopOrigId;
        }
        if (!step.loopEndOrigId && routerLoopContext.loopEndOrigId) {
          step.loopEndOrigId = routerLoopContext.loopEndOrigId;
        }
      }

      // Prefer uiData parent loop info when available.
      const uiParentLoopOrigId = getParentLoopOrigId(step);
      if (uiParentLoopOrigId && step.parentLoopOrigId !== uiParentLoopOrigId) {
        step.parentLoopOrigId = uiParentLoopOrigId;
      }

      // Available Output
      if (isStep && !hasExplicitEdges) {
        // Use incremental accumulators instead of rescanning findingList per step.
        const afterOutputs = cumulativeOutputs
          .filter((x) => isLoopOutputAllowed(step, x))
          .map((x) => ({ uniqueId: x.uniqueId, id: x.id }));

        const afterMeta = cumulativeMeta
          .filter((x) => isLoopOutputAllowed(step, x))
          .map((x) => ({ uniqueId: x.uniqueId, id: x.id }));

        if (subPosition === 0) {
          step.availableOutput = afterOutputs;
          step.availableMetaoutput = afterMeta;
        } else if (subPosition > 0) {
          const prevOutputs = Array.isArray(previousStep?.availableOutput)
            ? previousStep!.availableOutput
            : [];
          const prevMeta = Array.isArray(previousStep?.availableMetaoutput)
            ? previousStep!.availableMetaoutput
            : [];

          const filteredPrevOutputs = prevOutputs.filter((item) => {
            const candidate = stepByUniqueId.get(normalizeKey(item?.uniqueId));
            if (!candidate) return true;
            return isLoopOutputAllowed(step, candidate);
          });
          const filteredPrevMeta = prevMeta.filter((item) => {
            const candidate = stepByUniqueId.get(normalizeKey(item?.uniqueId));
            if (!candidate) return true;
            return isLoopOutputAllowed(step, candidate);
          });

          step.availableOutput = [...filteredPrevOutputs, ...afterOutputs];
          step.availableMetaoutput = [...filteredPrevMeta, ...afterMeta];
        }

        // Accumulate AFTER computing this step's available outputs
        if (step.output) cumulativeOutputs.push(step);
        if (step.metaoutput) cumulativeMeta.push(step);
      }

      // Routes
      if (isRoutes) {
        const newTotalIndex = index + totalIndex;
        const nextRouterContext = currentRouterContext?.insideLoop
          ? currentRouterContext
          : routerLoopContext;
        addPositionToStep({
          findingList: step.routes,
          subPosition,
          totalIndex: newTotalIndex,
          isStep: false,
          conditionPosition: { x: newTotalIndex, y: subPosition },
          subStepUniqueId: step.uniqueId,
          routeIndex,
          previousStep: step,
          routerLoopContext: nextRouterContext,
        });
      }
      // Route
      if (isRoute) {
        addPositionToStep({
          findingList: step.steps,
          subPosition,
          totalIndex,
          isStep: true,
          conditionPosition,
          subStepUniqueId,
          routeIndex: index,
          previousStep,
          routerLoopContext,
        });
      }
    });
  }

  addPositionToStep({ findingList: newSteps });

  if (hasExplicitEdges) {
    return computeAvailableOutput(newSteps, edges);
  }

  return newSteps;
}
